/**
 * Separates hashtags that were added via the tag selector (stored in the
 * `hashtags` array on the post) from the caption text.
 *
 * The API's `appendHashtagsToText` appends `#tag` tokens to the end of the
 * caption `BodyText` for every separately-selected hashtag that isn't already
 * present. On the read side, `post.text` therefore contains the caption plus
 * those appended tags, and `post.hashtags` holds the separately-stored list.
 *
 * This function strips the trailing `#tag` tokens from `post.text` that match
 * `post.hashtags`, so the caption can be rendered as the user typed it and the
 * separately-added tags can be shown on a new line below. Tags the user typed
 * inline are left untouched — `appendHashtagsToText` skips a tag that is
 * already in the text, so it never appears twice, and a tag that was typed
 * inline but also selected separately will only be stripped if it sits at the
 * very end of the text (where the API would have appended it).
 */

import { formatHashtag, normalizeHashtag } from './hashtags';

export interface SeparateHashtagsResult {
  /** The caption text with separately-stored hashtags stripped from the end. */
  caption: string;
  /** The stripped hashtags as `#tag` strings, in their original trailing order. */
  separateHashtags: string[];
}

/**
 * Same token shape as `hashtags.ts`: letters, digits and underscore, any script.
 * A repeated `#` is accepted so posts saved by the build that appended a second
 * one still resolve to their tag instead of being read as plain caption text.
 */
const HASHTAG_TOKEN = /^#+([\p{L}\p{N}_]+)$/u;

/**
 * Strips trailing `#tag` tokens from `text` that match any entry in
 * `hashtags` (case-insensitive). Returns the remaining caption and the
 * stripped tags, each with exactly one `#`. When `hashtags` is empty the input
 * is returned unchanged.
 */
export function splitSeparateHashtags(
  text: string,
  hashtags: string[] | null | undefined,
): SeparateHashtagsResult {
  if (!hashtags || hashtags.length === 0) {
    return { caption: text, separateHashtags: [] };
  }

  const tagSet = new Set(hashtags.map(normalizeHashtag));
  const trimmed = text.trimEnd();
  const tokens = trimmed.split(/\s+/);
  const stripped: string[] = [];

  while (tokens.length > 0) {
    const last = tokens[tokens.length - 1];
    const match = last.match(HASHTAG_TOKEN);
    if (match && tagSet.has(normalizeHashtag(match[1]))) {
      tokens.pop();
      stripped.unshift(formatHashtag(match[1]));
    } else {
      break;
    }
  }

  if (stripped.length === 0) {
    return { caption: text, separateHashtags: [] };
  }

  return {
    caption: tokens.join(' ').trimEnd(),
    separateHashtags: stripped,
  };
}
